import React, { Component } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { DownloadTheApp, TrustpilotReviews } from 'wanderu-app-gettheapp';
import {
    BackToTopButton,
    FAQSection,
    LongFormContent,
    Section,
    ValueProps,
    ValueStatement,
} from 'wanderu-components';
import {
    CITY_NAV_ITEMS,
} from 'wanderu-ducks-seo';
import { localize } from 'wanderu-i18n';
import { isSessionStorageAvailable } from 'wanderu-utils';

import placeGuideConfig from '../../../config/placeguide.locale';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import CarrierSection from '../CarrierSection';
import BlurbSection from '../EditorialBlurbs/BlurbSection/BlurbSection';
import EditorialBlurbs from '../EditorialBlurbs/EditorialBlurbs';
import GeneralSection from '../GeneralSection/GeneralSection';
import InfoSection from '../InfoSection/InfoSection';
import LinkSection from '../LinkSection/LinkSection';
import LinkTable from '../LinkTable/LinkTable';
import TableOfContentsSection from '../TableOfContents/TableOfContents';
import CheapestTicket from './CheapestTicket/CheapestTicket';
import MainStations from './MainStations/MainStations';
import PlaceGuideHeader from './PlaceGuideHeader/PlaceGuideHeader';
import PopularRoutes from './PopularRoutes/PopularRoutes';
import Quickview from './Quickview/Quickview';

import styles from './PlaceGuide.scss';

class PlaceGuide extends Component {
    componentDidMount() {
        const {
            travelMode,
        } = this.props;

        if (!isSessionStorageAvailable()) {
            return;
        }

        window.sessionStorage.setItem('seo_referrer', travelMode);
    }

    isTranslated = (title, description) => {
        return this.hasTranslation(title) && this.hasTranslation(description);
    };

    hasTranslation = (str) => {
        const { t } = this.props;

        return t(str) !== str;
    };

    generateNavItemsList = ({
        showAbroadSection,
        showBlogBlurbSection,
        showCarriers,
        showCityBlurbSection,
        showFaqSection,
        showGeneralSection,
        showGetAroundSection,
        showPointOfInterestSection,
    }) => {
        const {
            generalSectionContent,
        } = this.props;

        const navItems = [];

        if (this.hasTranslation(CITY_NAV_ITEMS.quickview.text)) {
            navItems.push(CITY_NAV_ITEMS.quickview);
        }

        if (showCityBlurbSection) {
            navItems.push(CITY_NAV_ITEMS.cityBlurb);
        }

        if (showGeneralSection) {
            const {
                nav,
                title,
                description,
            } = generalSectionContent[0];

            if (this.isTranslated(title, description)) {
                navItems.push(nav);
            }
        }

        if (showCarriers) {
            navItems.push({
                anchor: '#carrier-section',
                text: 'tocLinkCarriers',
            });
        }

        if (showGeneralSection) {
            generalSectionContent.slice(1).forEach((section) => {
                if (this.isTranslated(section.title, section.description) && !!section.nav) {
                    navItems.push(section.nav);
                }
            });
        }

        if (showFaqSection) {
            navItems.push(CITY_NAV_ITEMS.faq);
        }

        if (showGetAroundSection) {
            navItems.push(CITY_NAV_ITEMS.getAround);
        }

        if (showPointOfInterestSection) {
            navItems.push(CITY_NAV_ITEMS.poi);
        }

        if (showAbroadSection) {
            navItems.push(CITY_NAV_ITEMS.abroad);
        }

        if (showBlogBlurbSection) {
            navItems.push(CITY_NAV_ITEMS.blog);
        }

        return navItems;
    };

    showFaqSection = () => {
        const {
            c2cRoutesData,
            infoSectionContent,
            showCheapestTicket,
            showInfoSection,
            showMainStations,
            showPopularRoutes,
        } = this.props;

        const cheapestTicketSectionIsValid = showCheapestTicket
            && this.isTranslated('cheapestTicketsTitle', 'cheapestTicketsDescription');

        const mainStationsSectionIsValid = showMainStations
            && this.isTranslated('mainStationsTitle', 'mainStationsDescription');

        const infoSectionIsValid = showInfoSection && !!infoSectionContent.length;

        const popularRoutesSectionIsValid = showPopularRoutes && !!c2cRoutesData.length;

        return cheapestTicketSectionIsValid || mainStationsSectionIsValid
            || infoSectionIsValid || popularRoutesSectionIsValid;
    };

    render() {
        const {
            blurbSectionContent,
            breadcrumbData,
            c2cRoutesData,
            carrierData,
            cheapestTicketData,
            content,
            generalSectionContent,
            hideCarrierStatsTable,
            infoSectionContent,
            interlinks,
            locale,
            locationData,
            longFormContent,
            quickviewData,
            routeData,
            showBlogBlurbSection,
            showBreadcrumbs,
            showCarriers,
            showCheapestTicket,
            showCityBlurbSection,
            showGeneralSection,
            showInfoSection,
            showInterlinkSection,
            showMainStations,
            showPopularRoutes,
            showRoutesSection,
            showStationBlurbSection,
            stats,
            t,
            travelMode,
        } = this.props;
        const {
            headerImageUrl,
            interlinkCityName,
            onTimeTitle,
            onTimeText,
            transitInfoTitle,
            transitInfoDescription,
            poiTitle,
            poiDescription,
            countryAbroadTitle,
            countryAbroadDescription,
        } = content;

        const showFaqSection = this.showFaqSection();
        const showInterlinks = showRoutesSection || showInterlinkSection;
        const showOnTimeSection = !!onTimeTitle && !!onTimeText;
        const showGetAroundSection = !!transitInfoTitle && !!transitInfoDescription;
        const showPointOfInterestSection = !!poiTitle && !!poiDescription;
        const showAbroadSection = !!countryAbroadTitle && !!countryAbroadDescription;
        const hasInterlinks = routeData.length
            || interlinks.carriers
            || interlinks.c2cs
            || interlinks.euc2c
            || interlinks.cities
            || interlinks.states;

        const navItems = this.generateNavItemsList({
            showAbroadSection,
            showBlogBlurbSection,
            showCarriers,
            showCityBlurbSection,
            showFaqSection,
            showGeneralSection,
            showGetAroundSection,
            showPointOfInterestSection,
        });

        const showNavSection = navItems.length > 1;

        return (
            <main>
                <PlaceGuideHeader
                    backgroundImage={headerImageUrl}
                    className={classNames(
                        styles.heroHeader,
                        { [styles.noPadding]: showNavSection },
                    )}
                    locationData={locationData}
                    travelMode={travelMode}
                />
                {showNavSection && (
                    <TableOfContentsSection
                        namespace={placeGuideConfig.namespace}
                        navItems={navItems}
                    />
                )}
                <Quickview quickviewData={quickviewData} />
                <Section>
                    <ValueProps />
                </Section>
                <TrustpilotReviews locale={locale} />
                <DownloadTheApp campaign="web-place_guide" />
                {!!longFormContent.sections && !!longFormContent.sections.length && (
                    <LongFormContent
                        {...longFormContent}
                        namespace={placeGuideConfig.namespace}
                        useSEOStyles
                    />
                )}
                {showCityBlurbSection && (
                    <EditorialBlurbs
                        blurbContent={blurbSectionContent}
                        departureDescription={t('cityDescription')}
                        label="city-editorial-blurb"
                        namespace={placeGuideConfig.namespace}
                        title={t('aboutCityTitle')}
                        variant="cities"
                        showDepartSection
                    />
                )}
                {showStationBlurbSection && (
                    <EditorialBlurbs
                        blurbContent={blurbSectionContent}
                        departureDescription={t('popularStationsDescription')}
                        departureStationsSubtitle={t('popularStationsSubtitle')}
                        departureSubtitle={
                            t('popularStationsDepartureTitle') === 'popularStationsDepartureTitle'
                                ? ''
                                : t('popularStationsDepartureTitle')
                        }
                        label="station-editorial-blurb"
                        namespace={placeGuideConfig.namespace}
                        title={t('popularStationsTitle')}
                        variant="stations"
                        showDepartSection
                    />
                )}
                {showGeneralSection && (
                    <GeneralSection
                        description={t(generalSectionContent[0].description)}
                        id={generalSectionContent[0].id}
                        key={generalSectionContent[0].title}
                        show={t(generalSectionContent[0].title) !== generalSectionContent[0].title
                            && t(generalSectionContent[0].description) !== generalSectionContent[0].description}
                        title={t(generalSectionContent[0].title)}
                    />
                )}
                {showCarriers && (
                    <CarrierSection
                        data={carrierData}
                        description={t('carrierSectionDescription')}
                        hideCarrierStatsTable={hideCarrierStatsTable}
                        namespace={placeGuideConfig.namespace}
                        subheading={t('busLines')}
                        title={t('carrierSectionTitle')}
                    />
                )}
                {showGeneralSection && (
                    <>
                        {generalSectionContent.slice(1).map(({ id, title, description }) => (
                            <GeneralSection
                                description={t(description)}
                                id={id}
                                key={title}
                                show={t(title) !== title && t(description) !== description}
                                title={t(title)}
                            />
                        ))}
                    </>
                )}
                {showOnTimeSection && (
                    <GeneralSection
                        description={onTimeText}
                        id="on-time-section"
                        show={true}
                        title={onTimeTitle}
                    />
                )}

                {showFaqSection && (
                    <FAQSection title={t('faqHeader')}>
                        {showCheapestTicket && (
                            <CheapestTicket
                                cheapestTicketData={cheapestTicketData}
                            />
                        )}
                        {showMainStations && <MainStations />}
                        {showInfoSection && infoSectionContent.map((info) => (
                            <InfoSection
                                id={info.prefix}
                                infoText={info.text}
                                infoTitle={info.title}
                                key={info.prefix}
                                metrics={{
                                    ...(cheapestTicketData ?? {}),
                                    ...(stats.metrics ?? {}),
                                }}
                                namespace={placeGuideConfig.namespace}
                                textTranslationKey={info.key}
                            />
                        ))}
                        {showPopularRoutes && (
                            <PopularRoutes
                                c2cRoutes={c2cRoutesData}
                            />
                        )}
                    </FAQSection>
                )}
                {showGetAroundSection && (
                    <GeneralSection
                        description={transitInfoDescription}
                        id="get-around-section"
                        show={true}
                        title={transitInfoTitle}
                    />
                )}
                {showPointOfInterestSection && (
                    <GeneralSection
                        description={poiDescription}
                        id="point-of-interest-section"
                        show={true}
                        title={poiTitle}
                    />
                )}
                {(showAbroadSection && <GeneralSection
                    description={t('countryAbroadDescription')}
                    id="abroad-section"
                    show={true}
                    title={t('countryAbroadTitle')}
                >
                    <BlurbSection
                        description={t('cityAbroadDescription')}
                        namespace={placeGuideConfig.namespace}
                        subtitle={t('cityAbroadTitle')}
                    />
                </GeneralSection>
                )}
                {showBlogBlurbSection && (
                    <EditorialBlurbs
                        blurbContent={blurbSectionContent}
                        label="blog-section"
                        namespace={placeGuideConfig.namespace}
                        title={t('blogTitle')}
                        variant="blog"
                        showDepartSection
                    />
                )}
                {showInterlinks && hasInterlinks && (
                    <LinkSection>
                        {showRoutesSection && routeData.length && (
                            <LinkTable
                                data={routeData}
                                title={t('linkSectionTitle')}
                                native
                            />
                        )}
                        {showInterlinkSection && interlinks.carriers && (
                            <LinkTable
                                data={interlinks.carriers.map((data) => ({
                                    linkAnchorText: data.carrierName,
                                    linkUrl: data.linkUrl,
                                }))}
                                title={t('interlinkToCarrierTitle')}
                            />
                        )}
                        {showInterlinkSection && interlinks.c2cs && (
                            <LinkTable
                                data={interlinks.c2cs}
                                title={t('interlinkToC2cTitle')}
                                native
                            />
                        )}
                        {showInterlinkSection && interlinks.euc2c && (
                            <LinkTable
                                data={interlinks.euc2c}
                                title={t('interlinkToEuC2cTitle')}
                                native
                            />
                        )}
                        {showInterlinkSection && interlinks.cities && (
                            <LinkTable
                                data={interlinks.cities}
                                title={t('interlinkToCityTitle', { interlinkCityName })}
                            />
                        )}
                        {showInterlinkSection && interlinks.states && (
                            <LinkTable
                                data={interlinks.states}
                                title={t('interlinkToStateTitle')}
                            />
                        )}
                    </LinkSection>
                )}
                <ValueStatement namespace={placeGuideConfig.namespace} />
                {showBreadcrumbs && breadcrumbData && (
                    <Breadcrumbs data={breadcrumbData} />
                )}
                <BackToTopButton />
            </main>
        );
    }
}

PlaceGuide.propTypes = {
    blurbSectionContent: PropTypes.shape({
        arriveCityImages: PropTypes.arrayOf(PropTypes.object),
        arriveStationImages: PropTypes.arrayOf(PropTypes.object),
        arriveStations: PropTypes.arrayOf(PropTypes.object),
        blogPosts: PropTypes.arrayOf(PropTypes.object),
        departCityImages: PropTypes.arrayOf(PropTypes.object),
        departStationImages: PropTypes.arrayOf(PropTypes.object),
        departStations: PropTypes.arrayOf(PropTypes.object),
    }),
    breadcrumbData: PropTypes.shape({
        activeIndex: PropTypes.shape({
            name: PropTypes.string.isRequired,
        }).isRequired,
        toCity: PropTypes.shape({
            name: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        }),
        toCountry: PropTypes.shape({
            code: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        }),
        toState: PropTypes.shape({
            code: PropTypes.string,
            name: PropTypes.string,
            url: PropTypes.string,
        }),
        transMode: PropTypes.shape({
            name: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        }).isRequired,
    }),
    c2cRoutesData: PropTypes.arrayOf(PropTypes.object),
    carrierData: PropTypes.arrayOf(PropTypes.object),
    cheapestTicketData: PropTypes.object,
    content: PropTypes.object.isRequired,
    generalSectionContent: PropTypes.arrayOf(
        PropTypes.shape({
            description: PropTypes.string,
            id: PropTypes.string,
            nav: PropTypes.shape({
                anchor: PropTypes.string,
                text: PropTypes.string,
            }),
            title: PropTypes.string,
        })
    ),
    hideCarrierStatsTable: PropTypes.bool,
    infoSectionContent: PropTypes.arrayOf(PropTypes.object),
    interlinks: PropTypes.shape({
        c2cs: PropTypes.arrayOf(
            PropTypes.shape({
                linkAnchorText: PropTypes.string,
                linkUrl: PropTypes.string,
            })
        ),
        carriers: PropTypes.arrayOf(
            PropTypes.shape({
                carrierCode: PropTypes.string,
                carrierName: PropTypes.string,
                linkUrl: PropTypes.string,
            })
        ),
        cities: PropTypes.arrayOf(
            PropTypes.shape({
                linkAnchorText: PropTypes.string,
                linkUrl: PropTypes.string,
            })
        ),
        euc2c: PropTypes.arrayOf(
            PropTypes.shape({
                linkAnchorText: PropTypes.string,
                linkUrl: PropTypes.string,
            })
        ),
        states: PropTypes.arrayOf(
            PropTypes.shape({
                linkAnchorText: PropTypes.string,
                linkUrl: PropTypes.string,
            })
        ),
    }),
    locale: PropTypes.string,
    locationData: PropTypes.object,
    longFormContent: PropTypes.object,
    quickviewData: PropTypes.arrayOf(PropTypes.object).isRequired,
    routeData: PropTypes.arrayOf(PropTypes.object),
    showBlogBlurbSection: PropTypes.bool,
    showBreadcrumbs: PropTypes.bool,
    showCarriers: PropTypes.bool,
    showCheapestTicket: PropTypes.bool,
    showCityBlurbSection: PropTypes.bool,
    showGeneralSection: PropTypes.bool,
    showInfoSection: PropTypes.bool,
    showInterlinkSection: PropTypes.bool,
    showMainStations: PropTypes.bool,
    showPopularRoutes: PropTypes.bool,
    showRoutesSection: PropTypes.bool,
    showStationBlurbSection: PropTypes.bool,
    stats: PropTypes.shape({
        distance: PropTypes.object,
        metrics: PropTypes.object,
    }),
    t: PropTypes.func,
    travelMode: PropTypes.string,
};

PlaceGuide.defaultProps = {
    c2cRoutesData: [],
    carrierData: [],
    cheapestTicketData: {},
    content: {},
    generalSectionContent: [],
    hideCarrierStatsTable: false,
    infoSectionContent: [],
    interlinks: {},
    locationData: {},
    longFormContent: {},
    routeData: [],
    showBreadcrumbs: false,
    showCarriers: false,
    showCheapestTicket: false,
    showGeneralSection: false,
    showInfoSection: false,
    showInterlinkSection: false,
    showMainStations: false,
    showPopularRoutes: false,
    showRoutesSection: false,
    stats: {},
};

export { PlaceGuide };
export default localize(PlaceGuide, ['t'], placeGuideConfig.namespace);
